import type { RestRequest } from 'msw'
import { API_UPLOAD_MAX_BODY_BYTES } from '~/cynqueModules/uploadLimits'

/**
 * Request-body-size enforcement for the mock API.
 *
 * The mock accepted an upload of ANY size, which made a 413 structurally
 * impossible to produce in dev, in the e2e suite and in the unit suite — the
 * same shape of gap as the missing 409 next door in versionConflict.ts, and with
 * the same consequence: the first `RequestEntityTooLarge` anyone saw was a
 * Honeybadger fault from a real user (#133922044 / #133922043, 2026-08-29).
 *
 * ⚠️ The real limit is on the WHOLE request body, not on the file — Litestar
 * compares `request_max_body_size` against the content-length header. So this
 * measures the file part PLUS the JSON `payload` part rather than the file
 * alone; a mock that checked only `file.size` would accept bodies the server
 * rejects and would not reproduce the boundary case that matters.
 *
 * The measurement ignores boundary/part-header bytes (a few hundred), which the
 * mock cannot see. That makes it very slightly lenient — deliberately, in the
 * same spirit as versionConflict's lenient precondition: erring towards accepting
 * is a mock being imprecise, whereas erring towards rejecting would invent
 * failures the server would not produce.
 */
export function measureMockRequestBodyBytes(req: RestRequest): number {
  const contentType = req.headers.get('content-type') || ''
  if (!contentType.includes('multipart/form-data')) {
    return 0
  }
  const body = req.body as Record<string, string | File> | undefined
  if (!body) {
    return 0
  }
  return Object.values(body).reduce<number>((total, value) => {
    if (value instanceof File) {
      return total + value.size
    }
    return total + String(value ?? '').length
  }, 0)
}

/** Does this request exceed the API's request-body cap? */
export function isOverRequestBodyLimit(req: RestRequest): boolean {
  return measureMockRequestBodyBytes(req) > API_UPLOAD_MAX_BODY_BYTES
}

/**
 * The 413 body. Shaped after what Litestar returns for an uncaught
 * `RequestEntityTooLarge`; `PayloadTooLargeError` carries whatever arrives
 * through without depending on the fields.
 */
export function requestTooLargeBody() {
  return {
    status_code: 413,
    detail: 'Request Entity Too Large',
  }
}
